<template>
    <v-dialog v-model="deleteDialog" width="500" :disabled="disabled" persistent>
        <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="w-100">
                <slot></slot>
            </div>
        </template>
        <v-card>
            <v-card-title class="flex-nowrap align-start">
                Delete
                <v-spacer></v-spacer>
                <v-btn @click="deleteDialog = false" icon><v-icon>clear</v-icon></v-btn>
            </v-card-title>
            <v-card-text class="pt-3">
                {{ warningText() }}
                <ul class="mt-3">
                    <li v-for="app in selected" :key="app.aid">{{ app.long_id }}</li>
                </ul>
                <v-alert v-if="hasSharedApps" type="warning" text prominent class="mt-4">
                    <span class="font-weight-bold">This includes an app with a public URL</span>
                    <div class="mt-2 caption">
                        Deleting a shared application will remove access for everyone using its public URL, including anyone who has the link. This action
                        cannot be undone.
                    </div>
                    <v-checkbox v-model="sharedAppAgreeBox" color="warning" hide-details class="mt-3">
                        <template v-slot:label>
                            <span class="caption">I understand this will remove access for all users of the public URL</span>
                        </template>
                    </v-checkbox>
                </v-alert>
                <v-checkbox v-model="createSnapshotBeforeDelete" color="primary" hide-details class="mt-4">
                    <template v-slot:label>
                        <span class="caption">Create a snapshot before deleting</span>
                    </template>
                </v-checkbox>
            </v-card-text>
            <v-card-actions>
                <div v-if="fetchingDeids" class="caption">
                    <v-progress-circular indeterminate color="primary" size="16" />
                    <span class="ml-3">Fetching application data</span>
                </div>
                <v-checkbox v-else-if="envs > 0" color="error" v-model="deleteExportedApps">
                    <template v-slot:label>
                        <span class="caption">Also delete exported versions</span>
                    </template>
                </v-checkbox>
                <v-spacer></v-spacer>

                <v-btn color="error" text @click="deleteApps" :loading="loading" :disabled="fetchingDeids || (hasSharedApps && !sharedAppAgreeBox)">
                    Delete
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { snapshotEnums } from '@/mixins/snapshot'

export default {
    name: 'AppDeleteDialog',
    mixins: [snapshotEnums],
    data() {
        return {
            loading: false,
            deleteDialog: false,
            deleteExportedApps: true,
            fetchingDeids: false,
            envs: [],
            sharedAppAgreeBox: false,
            createSnapshotBeforeDelete: false
        }
    },
    props: {
        selected: {
            type: Array,
            default() {
                return []
            }
        },
        mode: { type: Number, default: null },
        disabled: { type: Boolean, default: false }
    },
    computed: {
        hasSharedApps() {
            return this.selected.some(app => app.shared === 'true' || app.shared === true)
        }
    },
    methods: {
        async createPreDeleteSnapshot() {
            const today = new Date()
            const currentDate = today.toJSON().slice(0, 10)
            const currentTime = ('0' + today.getHours()).slice(-2) + ':' + ('0' + today.getMinutes()).slice(-2) + ':' + ('0' + today.getSeconds()).slice(-2)
            const datetimeLongId = currentDate + ' / ' + currentTime
            const datetimeShortId = datetimeLongId.replace(/\/+/g, '_').replace(/\s+/g, '').replace('-', '_').replace(/:/g, '_')
            const postBody = {
                description: 'Automatic snapshot taken before deleting applications',
                long_id: 'Pre-deletion snapshot ' + datetimeLongId,
                short_id: 'pre_delete_' + datetimeShortId,
                snapshot_type: this.snapshotTypes.QUICK_SNAPSHOT
            }
            try {
                await this.$axios.post(`/instances/${this.$route.params.iid}/snapshots_async`, postBody)
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to create a snapshot before deletion, proceeding with deleting the applications.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
        },
        async deleteApps() {
            this.loading = true
            try {
                if (this.createSnapshotBeforeDelete) {
                    await this.createPreDeleteSnapshot()
                }
                let url = `/instances/${this.$route.params.iid}/applications/delete`
                if (this.mode) {
                    url += `?mode=${this.mode}`
                }
                const request = { aids: this.selected.map(app => app.aid), delete_exported: this.deleteExportedApps }
                await this.$axios.post(url, request)
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to delete applications',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }

            this.$store.dispatch('snapshotStore/fetchApplications', this.$route.params.snid)
            const userTasksRoute = this.$router.resolve({ name: 'user-tasks' }).href
            this.$store.dispatch('showSnackBar', {
                snackBarText: `Applications successfully submitted for deletion, you can track their progress <a href="${userTasksRoute}">here</a>`,
                snackBarTimeout: 5000,
                snackBarIcon: 'check_circle'
            })

            this.$emit('finished')
            this.loading = false
            this.deleteDialog = false
        },
        // We might not even need this, have to talk about the differences between app delete and APPS delete
        async fetchEnvironmentsForApps() {
            this.fetchingDeids = true
            try {
                const promises = this.selected.map(app => {
                    // snapshots/snid/list_exports > returns all snapshot exports > filter
                    return this.$axios.get(`applications/${app.aid}/list_exports`)
                })
                const { data } = await Promise.all(promises)
                this.envs = data
            } catch {
                this.envs = []
            }
            this.fetchingDeids = false
        },
        warningText() {
            if (this.mode === 2) {
                return `Are you sure you want to delete all distributed versions of selected applications from the other instances permanently? The applications would still be available for you.`
            }
            if (this.mode === 1) {
                return `Are you sure you want to delete your and all distributed versions of selected applications permanently?`
            }
            return `Are you sure you want to delete selected applications permanently?`
        }
    },
    watch: {
        deleteDialog(dialog) {
            if (dialog) {
                this.sharedAppAgreeBox = false
                this.createSnapshotBeforeDelete = this.hasSharedApps
                this.fetchEnvironmentsForApps()
            }
        }
    }
}
</script>
